import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip,
  Tooltip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Gavel,
  Warning,
  AttachMoney,
  ContentCut,
  Business,
  Refresh,
  AccessTime,
  ReportProblem,
  Security,
  VerifiedUser,
  MenuBook,
  Description,
  People,
} from '@mui/icons-material';
import { useAppSelector } from '../store/store';

// Icon mapping for irregularity types
const IRREGULARITY_ICONS = {
  'vendor-verification': VerifiedUser,
  'competition-analysis': Gavel,
  'sole-source': Gavel,
  'low-competition': Warning,
  'excessive-pricing': AttachMoney,
  'split-purchase': ContentCut,
  'vendor-concentration': Business,
  'repeat-awards': Refresh,
  'timing-anomaly': AccessTime,
  'emergency-misuse': ReportProblem,
};

// Irregularity categories and types
const IRREGULARITY_CATEGORIES = {
  'Vendor Issues': [
    {
      key: 'vendor-verification',
      label: 'Vendor Verification',
      emoji: '✓',
      description: 'Verify vendor eligibility and history',
      isNewFeature: true
    },
    {
      key: 'vendor-concentration',
      label: 'Vendor Concentration',
      emoji: '🎯',
      description: 'Multiple contracts to same vendor'
    },
    {
      key: 'repeat-awards',
      label: 'Repeat Awards',
      emoji: '🔄',
      description: 'Same vendors receiving repeated contracts'
    }
  ],
  'Competition Issues': [
    {
      key: 'competition-analysis',
      label: 'Competition Analysis',
      emoji: '🔍',
      description: 'Comprehensive competition fraud detection'
    },
    {
      key: 'low-competition',
      label: 'Low Competition',
      emoji: '⚠️',
      description: 'Limited bidders (2-3 competitors)'
    }
  ],
  'Pricing Issues': [
    {
      key: 'excessive-pricing',
      label: 'Excessive Pricing',
      emoji: '💰',
      description: 'Items priced above market rates'
    },
    {
      key: 'split-purchase',
      label: 'Split Purchase Orders',
      emoji: '✂️',
      description: 'Orders split to avoid thresholds'
    }
  ],
  'Compliance Issues': [
    {
      key: 'timing-anomaly',
      label: 'Timing Anomalies',
      emoji: '⏱️',
      description: 'Suspicious timing patterns'
    },
    {
      key: 'emergency-misuse',
      label: 'Emergency Misuse',
      emoji: '🚨',
      description: 'Non-emergency items as emergency'
    }
  ]
};

interface SidebarNavigationProps {
  open: boolean;
  onClose?: () => void;
  variant?: 'temporary' | 'permanent';
  width?: number;
  collapsed?: boolean;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  open = true,
  onClose,
  variant = 'permanent',
  width = 280,
  collapsed = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';
  
  // Extract current irregularity type from URL
  const getCurrentType = () => {
    // Handle vendor verification route
    if (location.pathname.startsWith('/vendor/verification')) {
      return 'vendor-verification';
    }
    const match = location.pathname.match(/\/investigator\/(.+)/);
    return match ? match[1] : null;
  };

  const currentType = getCurrentType();

  const handleIrregularitySelect = (irregularityKey: string) => {
    // Special case for vendor-verification - navigate to dedicated vendor route
    if (irregularityKey === 'vendor-verification') {
      navigate('/vendor/verification');
    } else {
      navigate(`/investigator/${irregularityKey}`);
    }
    // Scroll to top when navigation changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClose && variant === 'temporary') {
      onClose();
    }
  };

  const handleHomeClick = () => {
    navigate('/');
    // Scroll to top when navigation changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClose && variant === 'temporary') {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ width, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          backgroundColor: 'primary.dark',
          color: 'white',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        {collapsed ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{ fontSize: '1.5rem' }}>🔍</span>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '1.1rem'
              }}
            >
              🔍 Search Investigator
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.8,
                fontSize: '0.7rem',
                mt: 0.5,
                display: 'block'
              }}
            >
              Patent Pending
            </Typography>
          </Box>
        )}
      </Box>

      {/* Home/Default Search */}
      <List sx={{ pt: 1 }}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "Standard Search - General purpose search" : ""} placement="right">
            <ListItemButton 
              onClick={handleHomeClick}
              selected={location.pathname === '/'}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 0 : undefined,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                <span style={{ fontSize: '1.2rem' }}>🔍</span>
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="Standard Search" 
                  secondary="General purpose search"
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Irregularity Categories */}
      {Object.entries(IRREGULARITY_CATEGORIES).map(([categoryName, irregularities]) => (
        <Box key={categoryName} sx={{ mb: 2 }}>
          {/* Category Header */}
          {!collapsed && (
            <Typography
              variant="subtitle2"
              sx={{
                px: 2,
                py: 1,
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {categoryName}
            </Typography>
          )}

          {/* Irregularity Items */}
          <List sx={{ pt: 0 }}>
            {irregularities.map((irregularity) => {
              const IconComponent = IRREGULARITY_ICONS[irregularity.key as keyof typeof IRREGULARITY_ICONS];
              const isSelected = currentType === irregularity.key;

              return (
                <ListItem key={irregularity.key} disablePadding>
                  <Tooltip 
                    title={collapsed ? `${irregularity.label} - ${irregularity.description}` : ""} 
                    placement="right"
                  >
                    <ListItemButton
                      onClick={() => handleIrregularitySelect(irregularity.key)}
                      selected={isSelected}
                      sx={{
                        borderRadius: 1,
                        mx: 1,
                        mb: 0.5,
                        py: 1,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 0 : undefined,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.light',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                        {IconComponent ? (
                          <IconComponent 
                            sx={{ 
                              fontSize: '1.2rem',
                              color: isSelected ? 'white' : 'primary.main'
                            }} 
                          />
                        ) : (
                          <span style={{ fontSize: '1.2rem' }}>{irregularity.emoji}</span>
                        )}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 500 }}>
                                {irregularity.label}
                              </Typography>
                              {(irregularity as any).isNewFeature && (
                                <Chip 
                                  label="NEW" 
                                  size="small" 
                                  color="success"
                                  sx={{ 
                                    fontSize: '0.6rem',
                                    height: '16px',
                                    fontWeight: 600,
                                  }} 
                                />
                              )}
                              {isSelected && (
                                <Chip 
                                  label="Active" 
                                  size="small" 
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    height: '18px',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    color: 'white'
                                  }} 
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                                fontSize: '0.75rem'
                              }}
                            >
                              {irregularity.description}
                            </Typography>
                          }
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Deep Research Reports */}
      <List sx={{ pt: 0 }}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? "Deep Research Reports - View AI fraud analysis reports" : ""} placement="right">
            <ListItemButton 
              onClick={() => navigate('/investigator/deep-research-reports')}
              selected={location.pathname === '/investigator/deep-research-reports'}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 0 : undefined,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                <Security 
                  sx={{ 
                    fontSize: '1.2rem',
                    color: location.pathname === '/investigator/deep-research-reports' ? 'white' : 'primary.main'
                  }} 
                />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: location.pathname === '/investigator/deep-research-reports' ? 600 : 500 
                      }}>
                        🔍 Deep Research Reports
                      </Typography>
                      {location.pathname === '/investigator/deep-research-reports' && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          sx={{ 
                            fontSize: '0.7rem',
                            height: '18px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                          }} 
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: location.pathname === '/investigator/deep-research-reports' ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                        fontSize: '0.75rem'
                      }}
                    >
                      AI-powered fraud analysis reports
                    </Typography>
                  }
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />

            {/* Documentation Link - Admin only */}
            <ListItem disablePadding>
              <Tooltip title={collapsed ? "Documentation" : ""} placement="right">
                <ListItemButton
                  onClick={() => {
                    window.open('http://172.16.8.32:3002', '_blank');
                    if (onClose && variant === 'temporary') {
                      onClose();
                    }
                  }}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 0 : undefined,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    <MenuBook
                      sx={{
                        color: 'warning.main',
                        fontSize: '1.2rem'
                      }}
                    />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Documentation
                          </Typography>
                          <Chip
                            label="ADMIN"
                            size="small"
                            color="warning"
                            sx={{
                              fontSize: '0.6rem',
                              height: '16px',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        >
                          System docs (opens new tab)
                        </Typography>
                      }
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            <ListItem disablePadding>
              <Tooltip title={collapsed ? "User Management - Admin only" : ""} placement="right">
                <ListItemButton
                  onClick={() => {
                    navigate('/admin/users');
                    if (onClose && variant === 'temporary') {
                      onClose();
                    }
                  }}
                  selected={location.pathname === '/admin/users'}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 1,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 0 : undefined,
                    '&.Mui-selected': {
                      backgroundColor: 'warning.light',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'warning.main',
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    <People
                      sx={{
                        fontSize: '1.2rem',
                        color: location.pathname === '/admin/users' ? 'white' : 'warning.main'
                      }}
                    />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{
                            fontWeight: location.pathname === '/admin/users' ? 600 : 500
                          }}>
                            User Management
                          </Typography>
                          <Chip
                            label="ADMIN"
                            size="small"
                            color="warning"
                            sx={{
                              fontSize: '0.6rem',
                              height: '16px',
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: location.pathname === '/admin/users' ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                            fontSize: '0.75rem'
                          }}
                        >
                          Manage users and invites
                        </Typography>
                      }
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </>
        )}
      </List>

      {/* Footer Info */}
      {!collapsed && (
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Select an irregularity type to begin investigation
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (variant === 'temporary') {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          transition: 'width 0.3s ease',
          overflowX: 'hidden'
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default SidebarNavigation;